import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';

const Description = styled.p`
  margin-bottom: 1.5rem;
`;

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

export default function TeamsProjectsDrawer({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const isMounted = useIsMounted();
  const [projects, setProjects] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: channels } = await api.www
        .workspaces(workspace.id)
        .integrations()
        .microsoft.teams.getProjectChannels();

      const { data: options } = channels.length
        ? await api.www
            .workspaces(workspace.id)
            .projects()
            .selectOptions({
              ids: channels.map((channel) => channel.projectId).join(','),
              activeOnly: false,
              limit: channels.length,
            })
        : { data: [] };

      if (!isMounted.current) return;
      const names = _.keyBy(options, 'id');
      setProjects(
        _.sortBy(
          channels.map((channel) => ({ ...channel, project: names[channel.projectId] })),
          (channel) => channel.project?.name,
        ),
      );
    })();
  }, [api, workspace.id, isMounted]);

  return (
    <Drawer isOpen title="Microsoft Teams Projects" width="60rem" onClose={onClose}>
      {(closeDrawer) => {
        if (!projects) return <PageLoader />;

        return (
          <>
            <Description>
              Projects that post updates to a Microsoft Teams channel. A project administrator picks the channel in the
              project&apos;s settings.
            </Description>

            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column>Project</Table.Column>
                <Table.Column>Channel</Table.Column>
              </Table.BoxHeader>

              <Table.Body>
                {projects.map((channel) => (
                  <Table.BoxRow key={channel.id} data-testid={`ms-teams-project-${channel.projectId}`}>
                    <Table.Cell>
                      {channel.project && (
                        <ProjectInfo>
                          <div>{channel.project.name}</div>
                          <small>{channel.project.client.name}</small>
                        </ProjectInfo>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      {channel.teamName} / {channel.channelName}
                    </Table.Cell>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>

            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={closeDrawer}>Close</CancelButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
